import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatCHF, formatApiError, formatDate, formatDateTime, PAYMENT_METHOD_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n/I18nContext";
import { GroupTimeline } from "../components/nivara/GroupTimeline";
import { StatusBadge } from "../components/nivara/StatusBadge";
import { toast } from "sonner";
import { PlusCircle, Gavel, CheckCircle2, Trash2, Lock } from "lucide-react";

export default function GroupDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [group, setGroup] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAddPart, setShowAddPart] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const [g, tl, ps, pays, aucs, us] = await Promise.all([
        api.get(`/groups/${id}`),
        api.get(`/groups/${id}/timeline`),
        api.get(`/groups/${id}/participants`),
        api.get(`/payments?group_id=${id}`),
        api.get(`/auctions?group_id=${id}`),
        api.get(`/users`),
      ]);
      setGroup(g.data);
      setTimeline(tl.data);
      setParticipants(ps.data);
      setPayments(pays.data);
      setAuctions(aucs.data);
      setUsers(us.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, [id]);

  const isAdmin = user?.role === "admin";
  const isController = user?.role === "controller";

  const confirmContribution = async (contribId) => {
    try {
      await api.post(`/contributions/${contribId}/confirm`, {});
      toast.success(t("group.toast.confirmed"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeParticipant = async (pid) => {
    if (!window.confirm(t("group.confirm_remove"))) return;
    try {
      await api.delete(`/participants/${pid}`);
      toast.success(t("group.toast.removed"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeGroup = async () => {
    if (!window.confirm(t("admin.groups.confirm_delete"))) return;
    try {
      await api.delete(`/groups/${id}`);
      toast.success(t("group.toast.deleted"));
      nav("/admin/groups");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!group) return <div className="p-10 text-center text-[#94A3B8]">{t("common.loading")}</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to={isController ? "/dashboard/controlling" : "/dashboard/admin"} className="text-xs tracking-widest uppercase text-[#94A3B8] hover:text-[#0F4C3A]">{t("common.back")}</Link>
          <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#0F4C3A] mt-3">{group.name}</h1>
          <div className="text-[#475569] mt-2">{group.shares} · {group.start_date} · {t("common.status")}: {group.status}</div>
        </div>
        {isAdmin && (
          <button onClick={removeGroup} data-testid="delete-group-button" className="px-4 py-2 rounded-md text-sm border border-[#FCA5A5] text-[#DC2626] hover:bg-[#FEE2E2] inline-flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> {t("group.delete")}
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-[#E2E8E4] mb-6 overflow-x-auto">
        {["overview", "participants", "contributions", "payments", "auctions"].map((tk) => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            data-testid={`tab-${tk}`}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition ${
              tab === tk ? "border-b-2 border-[#0F4C3A] text-[#0F4C3A]" : "text-[#94A3B8] hover:text-[#0F4C3A]"
            }`}
          >
            {tk === "overview" && t("group.tab.overview")}
            {tk === "participants" && `${t("group.tab.participants")} (${participants.length})`}
            {tk === "contributions" && t("group.tab.contributions")}
            {tk === "payments" && `${t("group.tab.payments")} (${payments.length})`}
            {tk === "auctions" && `${t("group.tab.auctions")} (${auctions.length})`}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          {isAdmin && <BidSettingsCard group={group} onSaved={load} />}
          <GroupTimeline timeline={timeline} currentMonth={group.current_month} />
        </div>
      )}

      {tab === "participants" && (
        <div>
          {isAdmin && (
            <button
              onClick={() => setShowAddPart(true)}
              data-testid="add-participant-button"
              className="nv-btn-primary px-4 py-2 rounded-md font-medium inline-flex items-center gap-2 mb-4"
            >
              <PlusCircle className="w-4 h-4" /> {t("group.add_participant")}
            </button>
          )}
          <div className="nv-border bg-white rounded-md divide-y divide-[#E2E8E4]">
            {participants.length === 0 && <div className="p-8 text-center text-[#94A3B8]">{t("group.no_participants")}</div>}
            {participants.map((p) => (
              <div key={p.id} className="px-5 py-4 flex items-center justify-between flex-wrap gap-3" data-testid={`admin-participant-${p.share_number}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#0F4C3A] text-white font-mono-nv font-semibold flex items-center justify-center">
                    #{p.share_number}
                  </div>
                  <div>
                    <div className="font-medium text-[#0F4C3A]">{p.user?.name} {p.split_user && <span className="text-xs text-[#D4AF37]">+ {p.split_user.name}</span>}</div>
                    <div className="text-xs text-[#475569]">{p.user?.username || p.user?.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.has_received && <span className="text-xs tracking-wider uppercase font-semibold text-[#2E7D32]">{t("group.received_in", { month: p.received_month })}</span>}
                  {p.approved_to_bid ? (
                    <span className="text-xs tracking-wider uppercase font-semibold text-[#0369A1]">{t("group.bid_eligible")}</span>
                  ) : (
                    <span className="text-xs tracking-wider uppercase font-semibold text-[#94A3B8] inline-flex items-center gap-1"><Lock className="w-3 h-3" /> {t("group.locked")}</span>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={async () => {
                          await api.patch(`/participants/${p.id}`, { approved_to_bid: !p.approved_to_bid });
                          await load();
                        }}
                        className="px-3 py-1.5 text-xs border border-[#E2E8E4] rounded hover:bg-[#F8FAF9]"
                      >
                        {p.approved_to_bid ? t("group.lock") : t("group.unlock")}
                      </button>
                      <button onClick={() => removeParticipant(p.id)} className="p-1.5 text-[#DC2626] hover:bg-[#FEE2E2] rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "contributions" && (
        <div className="nv-border bg-white rounded-md overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="border-b border-[#E2E8E4] bg-[#F8FAF9]">
              <tr className="text-left">
                <th className="nv-label p-4">{t("group.col.month")}</th>
                <th className="nv-label p-4">{t("group.col.volume")}</th>
                <th className="nv-label p-4">{t("group.col.deduction")}</th>
                <th className="nv-label p-4">{t("group.col.payout")}</th>
                <th className="nv-label p-4">{t("group.col.amount_per_share")}</th>
                <th className="nv-label p-4">{t("group.col.due_date")}</th>
                <th className="nv-label p-4">{t("group.col.bid_date")}</th>
                <th className="nv-label p-4">{t("group.col.status")}</th>
                <th className="nv-label p-4 text-right">{t("group.col.action")}</th>
              </tr>
            </thead>
            <tbody>
              {timeline.map((c) => (
                <tr key={c.id} className="border-b border-[#E2E8E4] hover:bg-[#F8FAF9]" data-testid={`contrib-row-${c.month}`}>
                  <td className="p-4 font-mono-nv font-semibold">{c.month}</td>
                  <td className="p-4 font-mono-nv">CHF {formatCHF(c.volume)}</td>
                  <td className="p-4 font-mono-nv text-[#D97706]">−CHF {formatCHF(c.deduction)}</td>
                  <td className="p-4 font-mono-nv text-[#2E7D32]">CHF {formatCHF(c.payout)}</td>
                  <td className="p-4 font-mono-nv font-semibold">CHF {formatCHF(c.amount_per_share)}</td>
                  <td className="p-4 text-sm">{formatDate(c.due_date)}</td>
                  <td className="p-4 text-sm whitespace-nowrap">{c.bid_date ? formatDateTime(c.bid_date) : "—"}</td>
                  <td className="p-4">
                    {c.confirmed ? (
                      <span className="text-xs font-semibold tracking-wider uppercase text-[#2E7D32] inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> {t("group.confirmed")}
                      </span>
                    ) : (
                      <span className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8]">{t("group.open")}</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    {isAdmin && !c.confirmed && (
                      <button
                        onClick={() => confirmContribution(c.id)}
                        data-testid={`confirm-contrib-${c.month}`}
                        className="px-3 py-1.5 text-xs nv-btn-primary rounded"
                      >
                        {t("group.confirm_notify")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payments" && (
        <div>
          {isAdmin && (
            <button
              onClick={() => setShowPayment({ participant_id: "", month: group.current_month, amount: 0, method: "bank", paid_by_user_id: "" })}
              data-testid="create-payment-button"
              className="nv-btn-primary px-4 py-2 rounded-md font-medium inline-flex items-center gap-2 mb-4"
            >
              <PlusCircle className="w-4 h-4" /> {t("group.create_payment")}
            </button>
          )}
          <div className="nv-border bg-white rounded-md divide-y divide-[#E2E8E4]">
            {payments.length === 0 && <div className="p-8 text-center text-[#94A3B8]">{t("group.no_payments")}</div>}
            {payments.map((p) => {
              const part = participants.find((pp) => pp.id === p.participant_id);
              const expected = timeline.find((c) => c.month === p.month)?.amount_per_share || 0;
              const sumPaid = payments
                .filter((x) => x.participant_id === p.participant_id && x.month === p.month && x.status !== "cancelled")
                .reduce((s, x) => s + x.amount, 0);
              return (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid={`payment-row-${p.id}`}>
                  <div>
                    <div className="font-medium text-[#0F4C3A]">#{part?.share_number} · {part?.user?.name || "—"}</div>
                    <div className="text-xs text-[#475569]">
                      {t("group.month_label")} {p.month} · {PAYMENT_METHOD_LABELS[p.method]} · {formatDate(p.paid_at)}
                      {p.paid_by_name && <span className="text-[#D4AF37] font-medium"> · {t("group.paid_by")} {p.paid_by_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-mono-nv font-semibold text-[#0F4C3A]">CHF {formatCHF(p.amount)}</div>
                    {expected > 0 && (
                      sumPaid >= expected ? (
                        <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded bg-[#F0FDF4] text-[#2E7D32]" data-testid={`fully-paid-${p.id}`}>
                          {t("group.fully_paid")}
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded bg-[#FEF3C7] text-[#92400E]" data-testid={`partial-paid-${p.id}`}>
                          {t("group.partial_paid")} · {formatCHF(sumPaid)} / {formatCHF(expected)}
                        </span>
                      )
                    )}
                    <StatusBadge status={p.status} />
                    {isController && p.status === "paid" && (
                      <button
                        onClick={async () => { await api.patch(`/payments/${p.id}`, { status: "checked" }); await load(); }}
                        data-testid={`check-payment-${p.id}`}
                        className="px-3 py-1.5 text-xs border border-[#0369A1] text-[#0369A1] rounded hover:bg-[#E0F2FE]"
                      >
                        {t("group.action_check")}
                      </button>
                    )}
                    {(isAdmin || isController) && p.status === "checked" && (
                      <button
                        onClick={async () => { await api.patch(`/payments/${p.id}`, { status: "released" }); await load(); }}
                        data-testid={`release-payment-${p.id}`}
                        className="px-3 py-1.5 text-xs nv-btn-gold rounded"
                      >
                        {t("group.action_release")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "auctions" && (
        <div>
          {isAdmin && (
            <div className="flex flex-wrap gap-3 mb-4">
              {timeline.filter((c) => c.month > 1).map((c) => {
                const existing = auctions.find((a) => a.month === c.month);
                const closed = existing?.status === "closed";
                return (
                  <button
                    key={c.month}
                    disabled={closed}
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/auctions/start", { group_id: id, month: c.month });
                        toast.success(`Bietverfahren Monat ${c.month} geöffnet.`);
                        window.location.href = `/auctions/${data.auction_id}`;
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    data-testid={`start-auction-${c.month}`}
                    className={`px-3 py-2 text-xs rounded-md border ${closed ? "bg-[#F1F5F9] text-[#94A3B8]" : "bg-white border-[#E2E8E4] hover:bg-[#F8FAF9]"}`}
                  >
                    Monat {c.month} {closed ? "✓" : existing ? "(offen)" : ""}
                  </button>
                );
              })}
            </div>
          )}
          <div className="nv-border bg-white rounded-md divide-y divide-[#E2E8E4]">
            {auctions.length === 0 && <div className="p-8 text-center text-[#94A3B8]">Noch keine Bietverfahren.</div>}
            {auctions.map((a) => (
              <Link
                key={a.id}
                to={`/auctions/${a.id}`}
                data-testid={`auction-row-${a.id}`}
                className="px-5 py-4 flex items-center justify-between hover:bg-[#F8FAF9]"
              >
                <div>
                  <div className="font-medium text-[#0F4C3A]">Monat {a.month}</div>
                  <div className="text-xs text-[#475569]">Status: {a.status}{a.winning_bid !== null && ` · Gewinngebot CHF ${formatCHF(a.winning_bid)}`}</div>
                </div>
                <Gavel className="w-4 h-4 text-[#0F4C3A]" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {showAddPart && (
        <AddParticipantModal
          onClose={() => setShowAddPart(false)}
          onAdded={async () => { setShowAddPart(false); await load(); }}
          groupId={id}
          shares={group.shares}
          users={users.filter((u) => u.role === "participant")}
          taken={participants.map((p) => p.share_number)}
        />
      )}
      {showPayment && (
        <CreatePaymentModal
          onClose={() => setShowPayment(null)}
          onCreated={async () => { setShowPayment(null); await load(); }}
          groupId={id}
          participants={participants}
          timeline={timeline}
          initial={showPayment}
        />
      )}
    </div>
  );
}

function BidSettingsCard({ group, onSaved }) {
  const t = useT();
  const [weekday, setWeekday] = useState(group.bid_weekday || "sunday");
  const [time, setTime] = useState(group.bid_time || "10:00");
  const [saving, setSaving] = useState(false);
  const dirty = weekday !== (group.bid_weekday || "sunday") || time !== (group.bid_time || "10:00");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/groups/${group.id}`, { bid_weekday: weekday, bid_time: time });
      toast.success(t("group.toast.settings_saved"));
      await onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="nv-border bg-white rounded-md p-4 sm:p-5" data-testid="bid-settings-card">
      <div className="nv-label">{t("group.bid_settings")}</div>
      <p className="text-xs text-[#94A3B8] mt-1">{t("group.bid_settings_hint")}</p>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex gap-2">
          {["saturday", "sunday"].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWeekday(d)}
              data-testid={`group-bid-weekday-${d}`}
              className={`px-5 py-2.5 border rounded-md text-sm font-medium transition ${
                weekday === d ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white border-[#E2E8E4] text-[#0F4C3A] hover:bg-[#F8FAF9]"
              }`}
            >
              {t(d === "saturday" ? "admin.create.bid_saturday" : "admin.create.bid_sunday")}
            </button>
          ))}
        </div>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          data-testid="group-bid-time-input"
          className="w-full sm:w-36 px-4 py-2.5 border border-[#E2E8E4] rounded-md font-mono-nv"
        />
        {dirty && (
          <button onClick={save} disabled={saving} data-testid="save-bid-settings" className="nv-btn-primary px-5 py-2.5 rounded-md text-sm font-medium">
            {saving ? "…" : t("group.save_settings")}
          </button>
        )}
      </div>
    </div>
  );
}

function AddParticipantModal({ onClose, onAdded, groupId, shares, users, taken }) {
  const t = useT();
  const [form, setForm] = useState({ user_id: "", share_number: 1, split_with_id: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.split_with_id) delete payload.split_with_id;
      await api.post(`/groups/${groupId}/participants`, payload);
      toast.success(t("group.toast.added"));
      onAdded();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-medium text-[#0F4C3A]">{t("group.modal.add_participant")}</h3>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="nv-label block mb-2">{t("group.modal.user")}</label>
            <select
              required
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              data-testid="add-part-user-select"
              className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md"
            >
              <option value="">{t("group.modal.choose")}</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.username || u.email})</option>)}
            </select>
          </div>
          <div>
            <label className="nv-label block mb-2">{t("group.modal.share")}</label>
            <select
              required
              value={form.share_number}
              onChange={(e) => setForm({ ...form, share_number: parseInt(e.target.value) })}
              data-testid="add-part-share-select"
              className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md"
            >
              {Array.from({ length: shares }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n} disabled={taken.includes(n)}>
                  #{n} {taken.includes(n) ? t("group.modal.taken") : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="nv-label block mb-2">{t("group.modal.split")}</label>
            <select
              value={form.split_with_id}
              onChange={(e) => setForm({ ...form, split_with_id: e.target.value })}
              data-testid="add-part-split-select"
              className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md"
            >
              <option value="">{t("group.modal.no_split")}</option>
              {users.filter((u) => u.id !== form.user_id).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.username || u.email})</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} data-testid="submit-add-part" className="flex-1 nv-btn-primary py-2.5 rounded-md">{loading ? "…" : t("group.modal.add_btn")}</button>
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-[#E2E8E4] rounded-md">{t("common.cancel")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreatePaymentModal({ onClose, onCreated, groupId, participants, timeline, initial }) {
  const t = useT();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const selPart = participants.find((p) => p.id === form.participant_id);
  const isSplit = !!selPart?.split_with_user_id;

  const suggestAmount = (pid, month) => {
    const part = participants.find((p) => p.id === pid);
    const contrib = timeline.find((c) => c.month === parseInt(month));
    if (!part || !contrib) return 0;
    return part.split_with_user_id ? Math.round(contrib.amount_per_share / 2) : contrib.amount_per_share;
  };

  const onPartChange = (pid) => setForm((f) => ({ ...f, participant_id: pid, paid_by_user_id: "", amount: suggestAmount(pid, f.month) }));
  const onMonthChange = (m) => setForm((f) => ({ ...f, month: m, amount: suggestAmount(f.participant_id, m) }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, group_id: groupId, amount: parseInt(form.amount || 0), month: parseInt(form.month) };
      if (!payload.paid_by_user_id) delete payload.paid_by_user_id;
      await api.post("/payments", payload);
      toast.success(t("group.toast.payment_added"));
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-medium text-[#0F4C3A]">{t("group.modal.payment_title")}</h3>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="nv-label block mb-2">{t("role.participant")}</label>
            <select
              required
              value={form.participant_id}
              onChange={(e) => onPartChange(e.target.value)}
              data-testid="payment-participant-select"
              className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md"
            >
              <option value="">{t("group.modal.choose")}</option>
              {participants.map((p) => (
                <option key={p.id} value={p.id}>
                  #{p.share_number} — {p.user?.name}{p.split_user ? ` + ${p.split_user.name} (½/½)` : ""}
                </option>
              ))}
            </select>
          </div>
          {isSplit && (
            <div>
              <label className="nv-label block mb-2">{t("group.modal.payer")}</label>
              <select
                required
                value={form.paid_by_user_id || ""}
                onChange={(e) => setForm({ ...form, paid_by_user_id: e.target.value })}
                data-testid="payment-payer-select"
                className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md"
              >
                <option value="">{t("group.modal.payer_choose")}</option>
                <option value={selPart.user_id}>{selPart.user?.name}</option>
                <option value={selPart.split_with_user_id}>{selPart.split_user?.name}</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="nv-label block mb-2">{t("dashboard.month")}</label>
              <input type="number" min="1" max="60" required value={form.month} onChange={(e) => onMonthChange(e.target.value)} className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md font-mono-nv" />
            </div>
            <div>
              <label className="nv-label block mb-2">{t("group.modal.amount")}</label>
              <input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md font-mono-nv" />
            </div>
          </div>
          <div>
            <label className="nv-label block mb-2">{t("group.modal.method")}</label>
            <div className="flex gap-2">
              {["cash", "twint", "bank"].map((m) => (
                <button key={m} type="button" onClick={() => setForm({ ...form, method: m })} data-testid={`payment-method-${m}`} className={`flex-1 py-2 border rounded-md text-sm ${form.method === m ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "border-[#E2E8E4]"}`}>
                  {t(`group.modal.method.${m}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} data-testid="submit-payment" className="flex-1 nv-btn-primary py-2.5 rounded-md">{loading ? "…" : t("group.modal.record")}</button>
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-[#E2E8E4] rounded-md">{t("common.cancel")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
